import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { BrandMark } from "@/components/BrandMark";
import { checkSignIn, safeNext, signIn, SignInError } from "@/lib/api/auth";
import { useAuth } from "@/spa/useAuth";

import styles from "./LoginPage.module.css";
import { PasswordInput } from "./PasswordInput";

/**
 * Sign in to omnium admin.
 *
 * Sends the email and password to POST /auth/login and keeps the token it
 * gives back. Afterwards the person goes back to the page that sent them here.
 * When sign-in is off on this omnium the page says so and lets them go straight in.
 */
export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (auth.status === "checking") void checkSignIn();
  }, [auth.status]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    const password = String(form.get("password") ?? "");
    if (!email || !password) {
      setError("Enter your email and your password.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate(next, { replace: true });
    } catch (thrown) {
      setError(thrown instanceof SignInError ? thrown.message : "Sign-in failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  let notice: React.ReactNode = null;
  if (auth.status === "ready" && auth.account) {
    notice = (
      <>
        You are signed in as <strong>{auth.account.name}</strong>. <Link to={next}>Go on</Link>
      </>
    );
  } else if (auth.status === "ready" && !auth.required) {
    notice = (
      <>
        Sign-in is off on this omnium, so you can go straight in.{" "}
        <Link to={next}>Open omnium</Link>
      </>
    );
  } else if (auth.status === "unreachable") {
    notice = <>omnium is not answering right now. You can still try to sign in.</>;
  }

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <section className={styles.heroPane} aria-hidden="true">
          <p className={styles.heroHeadline}>
            <em>Every sport,</em> one source of truth
          </p>
        </section>
        <section className={styles.formPane}>
          <form className={styles.formInner} onSubmit={(event) => void submit(event)} noValidate>
            <div className={styles.logo}>
              <BrandMark size={32} />
            </div>
            <h1 className={styles.title}>Welcome back</h1>
            <p className={styles.subtitle}>Sign in to omnium admin</p>
            <hr className={styles.divider} />
            {notice && (
              <p className={styles.notice} role="status">
                {notice}
              </p>
            )}
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="login-email">
                  Email
                </label>
                <input
                  className={styles.input}
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  placeholder="you@example.com"
                />
              </div>
              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label} htmlFor="login-password">
                    Password
                  </label>
                </div>
                <PasswordInput />
              </div>
            </div>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <button className={styles.submit} type="submit" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <p className={styles.footnote}>
              Need access? <strong>Contact an administrator</strong>
            </p>
          </form>
        </section>
      </div>
    </main>
  );
}
